import type { Option, Timescale } from "@tento-lona/sheets";
import { type SheetRow, type TimeFormat } from "@tento-lona";
import {
  type BindCellContext,
  type FixedChildRowContext,
  type RenderPlugin,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { NaiveDate, Weekday } from "@tento-chrono";
import { Constants } from "@tento-core/constants";
import { LiveData } from "@tento-core/livedata";
import { Div } from "@tento-ui/component-builtin";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { Typography } from "@tento-ui/ui/typography";
import { TIMELINE_LEGEND_INERT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";

// ── Constants ──

const TICK_HEIGHT_PX = 12;
const CURSOR_LINE_HEIGHT_PX = 12;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];


// ── Time formatting ──

function formatHour(hour: number, tf: TimeFormat): string {
  if (tf === "24h") {
    return `${hour}:00`;
  }
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

function formatHourShort(hour: number, tf: TimeFormat): string {
  if (tf === "24h") {
    return `${hour}`;
  }
  if (hour === 0) return "12a";
  if (hour === 12) return "12p";
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

function formatAmPm(hour: number, tf: TimeFormat): string {
  if (tf === "24h") {
    return hour < 12 ? "0:00" : "12:00";
  }
  return hour < 12 ? "am" : "pm";
}

// ── TickScale ──

/** One tick: `dayOffset` is in DAYS from the column's start date (fractions
 * are sub-day positions — for a one-day column it is simply the fraction of
 * the column width). An empty label renders the tick line alone. */
export type TickMark = {
  dayOffset: number;
  label: string;
};

/** A tick vocabulary for the timeline: where the marks go, how the hover
 * cursor reads. Built-in scales cover calendar time (hours / days / weeks);
 * an embed whose columns mean something else — e.g. a quantized live frame
 * where "today's column" is really the current 15-minute block — supplies
 * its own via `TimelineLegendInertPlugin.scaleOverride`. */
export type TickScale = {
  name: string;
  ticks(start: NaiveDate, totalDays: number): TickMark[];
  formatCursor(date: NaiveDate, fractionOfDay: number): string;
  snapDayOffset(dayOffset: number, totalDays: number): number;
};

function makeHourlyScale(
  intervalHrs: number,
  tf: TimeFormat,
  colWidthPx: number,
): TickScale {
  // Choose label density based on available px per tick
  const ticksPerDay = 24 / intervalHrs;
  const pxPerTick = colWidthPx / ticksPerDay;
  const useShort = pxPerTick < 35;

  return {
    name: `${intervalHrs}hr`,
    ticks(_start: NaiveDate, totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      const totalHours = totalDays * 24;
      for (let h = 0; h < totalHours; h += intervalHrs) {
        const hr = h % 24;
        out.push({
          dayOffset: h / 24,
          label: useShort ? formatHourShort(hr, tf) : formatHour(hr, tf),
        });
      }
      return out;
    },
    formatCursor(date: NaiveDate, fractionOfDay: number): string {
      const hour = Math.round(fractionOfDay * 24);
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      return `${weekday} ${formatHour(hour % 24, tf)}`;
    },
    snapDayOffset(dayOffset: number): number {
      const hourFrac = intervalHrs / 24;
      return Math.round(dayOffset / hourFrac) * hourFrac;
    },
  };
}

function makeAmPmScale(tf: TimeFormat): TickScale {
  return {
    name: "am-pm",
    ticks(_start: NaiveDate, totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      for (let d = 0; d < totalDays; d++) {
        out.push({ dayOffset: d, label: formatAmPm(0, tf) });
        out.push({ dayOffset: d + 0.5, label: formatAmPm(12, tf) });
      }
      return out;
    },
    formatCursor(date: NaiveDate, fractionOfDay: number): string {
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      const hour = Math.round(fractionOfDay * 24);
      return `${weekday} ${formatHour(hour % 24, tf)}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset * 2) / 2;
    },
  };
}

function makeDayScale(colWidthPx: number, totalDays: number): TickScale {
  const pxPerTick = colWidthPx / totalDays;
  const useLetter = pxPerTick < 25;
  return {
    name: "day",
    ticks(start: NaiveDate, _totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      for (let d = 0; d < totalDays; d++) {
        const date = start.addDays(d);
        out.push({
          dayOffset: d,
          label: useLetter
            ? (WEEKDAY_LETTERS[date.dayOfWeek.dow - 1] ?? "")
            : (WEEKDAYS[date.dayOfWeek.dow - 1] ?? ""),
        });
      }
      return out;
    },
    formatCursor(date: NaiveDate): string {
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      const month = MONTHS[date.mth - 1] ?? "";
      return `${weekday}, ${month} ${date.day1}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset);
    },
  };
}

function makeTwoDaysScale(): TickScale {
  return {
    name: "2-day",
    ticks(start: NaiveDate, totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      for (let d = 0; d < totalDays; d += 2) {
        const date = start.addDays(d);
        out.push({
          dayOffset: d,
          label: WEEKDAY_LETTERS[date.dayOfWeek.dow - 1] ?? "",
        });
      }
      return out;
    },
    formatCursor(date: NaiveDate): string {
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      const month = MONTHS[date.mth - 1] ?? "";
      return `${weekday}, ${month} ${date.day1}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset);
    },
  };
}

function makeWeekScale(colWidthPx: number, totalDays: number): TickScale {
  const numWeeks = Math.ceil(totalDays / 7);
  const pxPerTick = colWidthPx / numWeeks;
  const useMonth = pxPerTick > 40;
  return {
    name: "week",
    ticks(start: NaiveDate, _totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      // Week-aligned ticks only (Mondays). Emitting an extra `d === 0`
      // column-start tick collides with the first Monday when a column begins
      // mid-week (e.g. "May 1" beside "May 4"), and repeats at every
      // month-column boundary — the touching labels seen on resize into a
      // multi-column month view. Mondays are evenly 7 days apart, so no clash.
      for (let d = 0; d < totalDays; d++) {
        const date = start.addDays(d);
        if (date.dayOfWeek === Weekday.MON) {
          const month = MONTHS[date.mth - 1] ?? "";
          out.push({
            dayOffset: d,
            label: useMonth ? `${month} ${date.day1}` : `${date.day1}`,
          });
        }
      }
      return out;
    },
    formatCursor(date: NaiveDate): string {
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      const month = MONTHS[date.mth - 1] ?? "";
      return `${weekday}, ${month} ${date.day1}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset);
    },
  };
}

function makeTwoWeeksScale(): TickScale {
  return {
    name: "2-week",
    ticks(start: NaiveDate, totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      let weekCount = 0;
      // Week-aligned (Mondays) only — see makeWeekScale: a `d === 0` start tick
      // collides with the first Monday at each column boundary.
      for (let d = 0; d < totalDays; d++) {
        const date = start.addDays(d);
        if (date.dayOfWeek === Weekday.MON) {
          if (weekCount % 2 === 0) {
            const month = MONTHS[date.mth - 1] ?? "";
            out.push({ dayOffset: d, label: `${month} ${date.day1}` });
          }
          weekCount++;
        }
      }
      return out;
    },
    formatCursor(date: NaiveDate): string {
      const weekday = WEEKDAYS[date.dayOfWeek.dow - 1] ?? "";
      const month = MONTHS[date.mth - 1] ?? "";
      return `${weekday}, ${month} ${date.day1}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset);
    },
  };
}

function makeMonthScale(): TickScale {
  return {
    name: "month",
    ticks(start: NaiveDate, totalDays: number): TickMark[] {
      const out: TickMark[] = [];
      const end = start.addDays(totalDays);
      let current = NaiveDate.fromYmd1(start.yr, start.mth, 1).exp("the first of start's month");
      while (current.dse < end.dse) {
        if (current.dse >= start.dse) {
          out.push({
            dayOffset: current.dse - start.dse,
            label: MONTHS[current.mth - 1] ?? "",
          });
        }
        const next =
          current.mth < 12
            ? NaiveDate.fromYmd1(current.yr, current.mth + 1, 1)
            : NaiveDate.fromYmd1(current.yr + 1, 1, 1);
        const nextMonth = next.asOk();
        if (nextMonth == null) break;
        current = nextMonth;
      }
      return out;
    },
    formatCursor(date: NaiveDate): string {
      const month = MONTHS[date.mth - 1] ?? "";
      return `${month} ${date.day1}`;
    },
    snapDayOffset(dayOffset: number): number {
      return Math.round(dayOffset);
    },
  };
}

function resolveTickScale(
  timescale: Timescale,
  columnWidthPx: number,
  totalDays: number,
  tf: TimeFormat,
): TickScale {
  // Pick the finest granularity whose per-tick spacing stays label-safe. The
  // old fixed `columnWidthPx >` thresholds ignored how MANY ticks a column
  // actually holds, so a column could pack far more labels than it had room
  // for and they collided. The count depends on the column's day span:
  //   day:   24/interval ticks  (sub-day)
  //   week:  totalDays ticks    (one per day)
  //   month: totalDays daily, ~totalDays/7 weekly
  // Drive the choice off `columnWidthPx / tickCount >= MIN_TICK_PX` instead, so
  // labels never overlap at any column width (e.g. a ~30-day month column no
  // longer crams 30 daily labels into ~300px → ~10px each).
  const MIN_TICK_PX = 30;
  const fits = (tickCount: number) =>
    tickCount > 0 && columnWidthPx / tickCount >= MIN_TICK_PX;
  switch (timescale) {
    case "day": {
      for (const intervalHrs of [1, 3, 6]) {
        if (fits(24 / intervalHrs)) {
          return makeHourlyScale(intervalHrs, tf, columnWidthPx);
        }
      }
      // am-pm is 2 ticks/day; below that even two labels collide on very
      // narrow day columns (e.g. a 3-week day view ~55px/day → "am"/"pm"
      // touching), so fall back to a single per-day weekday label.
      if (fits(2)) return makeAmPmScale(tf);
      return makeDayScale(columnWidthPx, totalDays);
    }
    case "week":
      if (fits(totalDays)) return makeDayScale(columnWidthPx, totalDays);
      return makeTwoDaysScale();
    case "month":
      if (fits(totalDays)) return makeDayScale(columnWidthPx, totalDays);
      if (fits(totalDays / 7)) return makeWeekScale(columnWidthPx, totalDays);
      return makeTwoWeeksScale();
    case "year":
      return makeMonthScale();
  }
}

// ── Cursor element ──

function makeCursorElement(): { $root: HTMLElement; $label: HTMLElement } {
  const $root = Div.make();
  $root.assignStyles({
    position: "absolute",
    top: "0px",
    left: "0px",
    width: "0px",
    display: "none",
    flexDirection: "column",
    alignItems: "center",
    pointerEvents: "none",
    zIndex: "999",
    height: "100%",
  });

  const $line = Div.make();
  $line.assignStyles({
    width: "1px",
    height: CURSOR_LINE_HEIGHT_PX + "px",
    background: "var(--divider)",
    zIndex: "1",
  });
  $root.appendChild($line);

  const $label = Typography.p("");
  $label.assignStyles({
    "--font-size": "var(--text-8px)",
    "--margin-top": "0px",
    "--margin-bottom": "0px",
    color: "var(--text-pri)",
    whiteSpace: "nowrap",
    background: "var(--bg)",
    borderRadius: "4px",
    padding: "2px 6px",
    boxShadow: "0px 0.5px 2px 4px var(--bg)",
  });
  $root.appendChild($label);

  return { $root, $label };
}

// ── Tick element ──

function makeTickElement(label: string): HTMLElement {
  const $el = Div.make();
  $el.assignStyles({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    pointerEvents: "none",
  });

  const $line = Div.make();
  $line.assignStyles({
    width: "1px",
    height: TICK_HEIGHT_PX + "px",
    background: "var(--divider)",
  });
  $el.appendChild($line);

  if (label) {
    const $label = Typography.p(label);
    $label.assignStyles({
      "--font-size": "var(--text-8px)",
      "--margin-top": "2px",
      "--margin-bottom": "0px",
      color: "var(--text-ter)",
      whiteSpace: "nowrap",
    });
    $el.appendChild($label);
  }

  return $el;
}

// ── ContentX → Date ──

function contentXToColumn(
  contentX: number,
  handle: SheetDelegate.State,
  mapper: ColumnIndex.Mapper,
): Option<{
  date: NaiveDate;
  fractionOfDay: number;
  columnWidthPx: number;
}> {
  const visible = handle.visibleColumns;
  if (!visible) return null;

  for (
    let offset = visible.start.offset;
    offset <= visible.end.offset;
    offset++
  ) {
    const colOffset = handle.getColumnOffsetPx(offset);
    const colWidth = handle.getColumnWidthPx(offset);
    if (contentX >= colOffset && contentX < colOffset + colWidth) {
      const fraction = (contentX - colOffset) / colWidth;
      const key = ColumnIndex.Key.fromIndex(offset);
      const resolved = mapper.resolveKey(key).castToDefault();
      if (!resolved) return null;
      const pd = resolved.pd;
      const totalDays = pd.end.dse - pd.start.dse;
      const exactDayOffset = fraction * totalDays;
      const dayIndex = Math.floor(exactDayOffset);
      const fractionOfDay = exactDayOffset - dayIndex;
      return {
        date: pd.start.addDays(dayIndex),
        fractionOfDay,
        columnWidthPx: colWidth,
      };
    }
  }
  return null;
}

// ── Plugin ──

/** The inert-row plugin for a timeline's legend row. */
export class TimelineLegendInertPlugin
  implements RenderPlugin.Inert<HTMLElement, HTMLElement>
{
  static readonly spec: PerTypeSpec = TIMELINE_LEGEND_INERT_SPEC;

  readonly role = "inert";
  readonly id = "timeline-legend";
  readonly icon: HTMLTemplateElement = document.createElement("template");

  legendFactory = null;
  makeRowOptions = null;
  loadData = null;

  #cursorRevealed = false;
  #timeFormat: LiveData.Readable<TimeFormat>;

  /**
   * Consulted before the built-in `resolveTickScale` on every tick build and
   * cursor move; return null to fall through to the built-ins. A mutable
   * field rather than a constructor option so one plugin instance can serve
   * sheets with different vocabularies — the host swaps it when it binds a
   * different sheet (the thunk is re-invoked per build, so a swap takes
   * effect on the next bind, and a scale whose ticks depend on the clock
   * re-evaluates each time).
   */
  scaleOverride: Option<() => Option<TickScale>> = null;

  constructor(
    opts: Option<{
      timeFormat?: LiveData.Readable<TimeFormat>;
    }>,
  ) {
    this.#timeFormat = opts?.timeFormat ?? new LiveData<TimeFormat>("12h");
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeCell(): HTMLElement {
    return Div.make();
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext) {}

  onResize(_$cell: HTMLElement, _context: BindCellContext): void {}

  async fixedChildrenForRow(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Promise<SheetDelegate.FixedChildSpec[]> {
    if (row.surface !== "footer") return [];

    const renderedColumns = context.renderedColumns;
    if (!renderedColumns) return [];

    const timescale = context.sheet.timescale;
    const tf = this.#timeFormat.get();
    const specs: SheetDelegate.FixedChildSpec[] = [];

    for (
      let offset = renderedColumns.start.offset;
      offset <= renderedColumns.end.offset;
      offset++
    ) {
      const key = ColumnIndex.Key.fromIndex(offset);
      const resolved = context.mapper.resolveKey(key).castToDefault();
      if (!resolved) continue;

      const pd = resolved.pd;
      const totalDays = pd.end.dse - pd.start.dse;
      if (totalDays <= 0) continue;

      const columnWidthPx =
        context.handle.state.getColumnWidthPx(offset) ||
        context.resolvedWidthPx;
      const tickScale =
        (this.scaleOverride ? this.scaleOverride() : null) ??
        resolveTickScale(timescale, columnWidthPx, totalDays, tf);
      const tickMarks = tickScale.ticks(pd.start, totalDays);

      for (const tick of tickMarks) {
        const insetLeftPx = Math.round(
          (tick.dayOffset / totalDays) * columnWidthPx,
        );
        const absoluteDayOffset = pd.start.dse + Math.floor(tick.dayOffset);
        const subDayFrac = Math.round((tick.dayOffset % 1) * 1000);
        const id = `tl-tick-${row.id}-${absoluteDayOffset}-${subDayFrac}`;

        specs.push({
          id,
          x: {
            type: "date-range",
            startDate: pd.start,
            // INCLUSIVE last column, so `.addDays(-1)`: `pd.end` is the
            // column's EXCLUSIVE end (the next column's first day), but
            // `FixedChildSpec.x.endDate` names the last column the child
            // covers — `FixedChild.Node.updateColumnRangeFromDates` resolves
            // it and then adds a whole column via `subindex.inc()`
            // (`tento-ui-js/components/sheet/sheet-layout.ts`). Passing the
            // exclusive end therefore declared a TWO-column child for a
            // one-column tick, which both widened the box and kept the tick
            // alive one column past its own column in the cull tree. Every
            // other caller already converts: `static-child-manager` writes
            // `spanPd.end.addDays(-1)`, and `EventItemAllDay.time` is
            // inclusive by construction.
            endDate: pd.end.addDays(-1),
          },
          inset: { left: insetLeftPx },
          // A tick is a MARK, not a box — zero width, like the hover cursor,
          // so the 1px line `makeTickElement` centres lands ON the anchor.
          //
          // `"auto"` looks right and is not: `bindWidth` consults the
          // resolved column range before the declared behaviour, so an
          // "auto" tick is as wide as its column and the centred line sits
          // half a column right of `inset.left`. Measured with the exclusive
          // end date above that was a full column — box x=635 (correct
          // anchor) but width 145.1px on 72.6px columns, line at 707.1.
          widthBehavior: 0,
          makeElement: () => makeTickElement(tick.label),
        });
      }
    }

    return specs;
  }

  onBindRow(handle: SheetDelegate, row: SheetRow.Joined.Handle): void {
    if (row.surface !== "footer") return;
    if (this.#cursorRevealed && handle.fixedChild.hasFixedChild("tl-cursor")) {
      return;
    }
    this.#cursorRevealed = true;

    const { $root, $label } = makeCursorElement();
    const cursorX = new LiveData<number>(0);

    handle.fixedChild.revealLive(
      "tl-cursor",
      $root,
      { x: cursorX, y: row.id },
      "footer",
    );
    // Paint cursor above tick marks (both get MAX_Z_INDEX - 5 from _appendFixedChild)
    $root.style.zIndex = String(Constants.MAX_Z_INDEX - 4);

    const mapper = handle.state.sheet.indexRenderer.mapper;
    const timescale = handle.state.sheet.timescale;

    handle.state.pointerPosition.addListener($root, (pos) => {
      if (!pos) {
        $root.style.display = "none";
        return;
      }

      const result = contentXToColumn(pos.contentX, handle.state, mapper);
      if (!result) {
        $root.style.display = "none";
        return;
      }

      const tf = this.#timeFormat.get();
      const totalDays = 1; // cursor is within a single day
      const tickScale =
        (this.scaleOverride ? this.scaleOverride() : null) ??
        resolveTickScale(timescale, result.columnWidthPx, totalDays, tf);

      $root.style.display = "flex";
      cursorX.update(pos.contentX);
      $label.textContent = tickScale.formatCursor(
        result.date,
        result.fractionOfDay,
      );
    });
  }

  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}
